import { X, Mail, MapPin, Briefcase, Building, Zap, Target } from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import { Tabs, Tab, Box } from '@mui/material';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../../api/axios';
import { generateReferralStrategy } from '../../../redux/ContactSlice/contactSlice';
import ReferralStrategy from './ReferralStrategy';
import JobAcquisitionPlan from './JobAcquisitionPlan';
import { recommendStrategy } from '../../../utils/strategyRecommendation';
import { STRATEGY_NAME_BY_ID } from '../../../constants/strategyCatalog';

const LinkedInSVG = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
);

const RecruiterModal = ({
    job,
    data,
    onClose,
    handleSaveSelectedJobs,
    onSaveComplete = null,
    onAnalysisComplete,
    analysisAlreadyDone = false,
    preAnalyzedData = null,
    isOnboardingPreview = false,
    /** When set to 0 or 1, forces the referral modal tab during onboarding tour (Deployment vs Acquisition). */
    onboardingForcedTab = null
}) => {
    console.log('RecruiterModal rendered with data:', data);
    console.log('Job:', job?.companyName, 'organizationId:', job?.organizationId);
    console.log('Analysis already done:', analysisAlreadyDone);
    console.log('Pre-analyzed data provided:', !!preAnalyzedData);
    
    const [expandedPersonId, setExpandedPersonId] = useState(null);
    const [reasoningData, setReasoningData] = useState(preAnalyzedData || {});
    const [loadingReasoning, setLoadingReasoning] = useState({});
    const [batchAnalysisComplete, setBatchAnalysisComplete] = useState(analysisAlreadyDone || !!preAnalyzedData);
    const [batchAnalysisLoading, setBatchAnalysisLoading] = useState(false);
    // Local fallback overview captured from batch-analysis response when accountStrategy is not available
    const [batchOverview, setBatchOverview] = useState(null);
    // Tab state for toggling between Referral Strategy and Job Acquisition Plan
    // Default to tab 0 (Referral Strategy) to restore previous UI ordering
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        if (!isOnboardingPreview || onboardingForcedTab == null) return;
        if (onboardingForcedTab === 0 || onboardingForcedTab === 1) {
            setActiveTab(onboardingForcedTab);
            requestAnimationFrame(() => {
                modalContentRef.current?.scrollTo?.(0, 0);
            });
        }
    }, [isOnboardingPreview, onboardingForcedTab]);
    
    // Prevent duplicate batch analysis calls (React StrictMode runs effects twice)
    const batchAnalysisInitiated = useRef(false);
    
    // Ref to preserve scroll position when expanding/collapsing
    const modalContentRef = useRef(null);
    
    // Get user profile for better referral analysis
    const user = useSelector((state) => state.auth.user);
    const dispatch = useDispatch();
    
    // 🆕 Derive organization identifier from every possible source so strategy lookup stays stable
    const deriveOrganizationId = () => {
        if (job?.organizationId) return job.organizationId;
        if (job?.organization_id) return job.organization_id;
        if (job?.companyId?._id) return job.companyId._id;
        if (job?.companyId) return job.companyId;
        if (job?.company?.organizationId) return job.company.organizationId;
        if (job?.company?._id) return job.company._id;
        if (job?._id) return job._id;
        if (data?.organizationId) return data.organizationId;
        if (data?.organization_id) return data.organization_id;

        const firstHiring = data?.hiring_manager?.best_guesses?.[0];
        const firstRecruiter = data?.recruiters?.best_guesses?.[0];
        const fallbackContact = firstHiring || firstRecruiter;

        if (fallbackContact) {
            return (
                fallbackContact.organization_id ||
                fallbackContact._fullData?.organization_id ||
                fallbackContact._fullData?.organization?.linkedin_company_id ||
                fallbackContact._fullData?.organization?.id
            );
        }

        return null;
    };

    // 🆕 Get account referral strategy from Redux (try multiple ID fields)
    const orgId = deriveOrganizationId();
    const accountStrategy = useSelector((state) => {
        if (!orgId) return null;
        const strategy = state.contacts.referralStrategies[orgId];
        console.log('🔍 [Redux Selector] Looking for strategy:', {
            orgId,
            found: !!strategy,
            allStrategies: Object.keys(state.contacts.referralStrategies || {}),
            strategy: strategy
        });
        return strategy;
    });

    // Debug logging for strategy
    useEffect(() => {
        console.log('🎯 [RecruiterModal] Account Strategy State:', {
            hasOrganizationId: !!orgId,
            organizationId: orgId,
            jobKeys: Object.keys(job || {}),
            strategyExists: !!accountStrategy,
            loading: accountStrategy?.loading,
            hasData: !!accountStrategy?.data,
            error: accountStrategy?.error,
            strategy: accountStrategy,
            batchAnalysisComplete
        });
    }, [accountStrategy, orgId, batchAnalysisComplete]);

    // Perform batch analysis helper (defined before useEffect to avoid lint errors)
    async function performBatchAnalysis() {
        console.log("🔄 [RecruiterModal] Starting batch analysis...");
        console.log("   - Has onAnalysisComplete callback:", !!onAnalysisComplete);
        console.log("   - Job data:", job?.companyName);
        
        setBatchAnalysisLoading(true);

        try {
            const hm = data?.hiring_manager || {};
            const rc = data?.recruiters || {};
            const allContacts = [
                ...(hm?.best_guesses || []),
                ...(rc?.best_guesses || [])
            ];

            if (allContacts.length === 0) {
                console.warn('[RecruiterModal] No contacts to analyze');
                setBatchAnalysisComplete(true);
                setBatchAnalysisLoading(false);
                return;
            }
            
            console.log(`[RecruiterModal] Analyzing ${allContacts.length} contacts...`);

            const jobDetails = {
                companyName: job.companyName,
                jobPosition: job.jobPosition,
                jobLocation: job.jobLocation,
                jobDesc: job.jobDesc,
                job_department_if_any: job.department || null
            };

            const userProfile = user ? {
                name: user.name,
                jobTitle: user.jobTitle || user.currentTitle,
                currentTitle: user.currentTitle,
                experience: user.experience,
                skills: user.skills || [],
                education: user.education || [],
                experience_details: user.experience || [],
                resume: user.resume || '',
                preferredDepartment: user.preferredDepartment || [],
                preferredIndustry: user.preferredIndustry || [],
                previousIndustryYears: user.previousIndustryYears || []
            } : {};

            // 🆕 Dispatch account strategy generation in parallel with contact analysis
            const orgId = job.organizationId || job.companyId || job._id;
            console.log('🔍 [RecruiterModal] Strategy dispatch check:', {
                hasOrgId: !!orgId,
                orgId: orgId,
                hasAccountStrategy: !!accountStrategy,
                accountStrategyLoading: accountStrategy?.loading,
                jobKeys: Object.keys(job || {})
            });

            if (orgId && !accountStrategy) {
                console.log('🎯 [RecruiterModal] Triggering account referral strategy generation for:', orgId);
                dispatch(generateReferralStrategy({
                    jobId: job.id || job._id,
                    organizationId: orgId,
                    jobDetails,
                    userProfile,
                    allContacts: allContacts.map(contact => ({
                        name: contact.name,
                        title: contact.title,
                        department: contact.department || contact._fullData?.department,
                        location: contact.location,
                        overall_experience_years: contact._fullData?.overall_experience_years,
                        company_experience_years: contact._fullData?.company_experience_years,
                        summary: contact._fullData?.summary,
                        skills: contact._fullData?.skills,
                        education: contact._fullData?.education,
                        certifications: contact._fullData?.certifications,
                        email: contact.email,
                        profile_url: contact.profile_url,
                        followers: contact._fullData?.followers,
                        confidence_score: contact.confidence_score,
                        referral_probability: contact.referral_probability,
                        data_source: contact._fullData?.data_source,
                        _fullData: contact._fullData
                    }))
                }));
            } else if (!orgId) {
                console.warn('⚠️ [RecruiterModal] No organizationId found - strategy will not be generated');
            } else if (accountStrategy) {
                console.log('ℹ️ [RecruiterModal] Strategy already exists, skipping dispatch');
            }

            const response = await api.post('/api/recruitercontacts/batch-analyze-contacts', {
                allContacts: allContacts.map(contact => ({
                    name: contact.name,
                    title: contact.title,
                    department: contact.department || contact._fullData?.department,
                    location: contact.location,
                    overall_experience_years: contact._fullData?.overall_experience_years,
                    company_experience_years: contact._fullData?.company_experience_years,
                    email: contact.email,
                    profile_url: contact.profile_url,
                    confidence_score: contact.confidence_score,
                    referral_probability: contact.referral_probability
                })),
                jobDetails,
                userProfile
            });

            // Capture top-level overview so we can show a "Strategy" section even if accountStrategy (Redux) is not present
            if (response?.data) {
                setBatchOverview({
                    overallStrategy: response.data.overallStrategy || null,
                    topRecommendations: response.data.topRecommendations || [],
                    source: response.data.source || 'batch'
                });
            }
            const analysisMap = {};

            (hm?.best_guesses || []).forEach((person, idx) => {
                const contactResponse = response.data.contacts?.find(c => c.name === person.name);
                if (contactResponse?.aiAnalysis) {
                    const personId = `hiring_manager-${idx}`;
                    analysisMap[personId] = {
                        success: true,
                        reasoning: contactResponse.aiAnalysis,
                        source: response.data.source || 'deepseek'
                    };
                }
            });
            (rc?.best_guesses || []).forEach((person, idx) => {
                const contactResponse = response.data.contacts?.find(c => c.name === person.name);
                if (contactResponse?.aiAnalysis) {
                    const personId = `recruiters-${idx}`;
                    analysisMap[personId] = {
                        success: true,
                        reasoning: contactResponse.aiAnalysis,
                        source: response.data.source || 'deepseek'
                    };
                }
            });

            setReasoningData(analysisMap);
            setBatchAnalysisComplete(true);
            
            console.log("✅ [RecruiterModal] Batch analysis COMPLETE. Calling onAnalysisComplete with", Object.keys(analysisMap).length, "contacts");
            // Notify parent that analysis is complete with the analyzed data
            if (onAnalysisComplete) {
                onAnalysisComplete(analysisMap);
                console.log("✅ [RecruiterModal] onAnalysisComplete callback executed");
            } else {
                console.warn("⚠️ [RecruiterModal] onAnalysisComplete callback is NOT defined!");
            }
        } catch (error) {
            console.error('[Batch Analysis] Failed:', error);
            setBatchAnalysisComplete(true);
            
            // Even on error, notify parent so button can appear
            if (onAnalysisComplete) {
                onAnalysisComplete({});
            }
        } finally {
            setBatchAnalysisLoading(false);
        }
    }
    
    // Automatically analyze all contacts when modal opens (only once) - NON-BLOCKING
    useEffect(() => {
        if (preAnalyzedData) {
            console.log('✅ Using pre-analyzed data, skipping analysis');
            setReasoningData(preAnalyzedData);
            setBatchAnalysisComplete(true);
            return;
        }

        if (analysisAlreadyDone) {
            console.log('⏭️ Skipping analysis - already completed');
            setBatchAnalysisComplete(true);
            return;
        }

        if (!data || batchAnalysisComplete || batchAnalysisLoading) return;

        // Reset the initiated flag on each new data object so a fresh fetch always re-runs
        batchAnalysisInitiated.current = false;

        const timeoutId = setTimeout(() => {
            if (!batchAnalysisInitiated.current) {
                batchAnalysisInitiated.current = true;
                performBatchAnalysis();
            }
        }, 100);

        // Cleanup resets the flag so StrictMode's second invoke works correctly
        return () => {
            clearTimeout(timeoutId);
            batchAnalysisInitiated.current = false;
        };
    }, [data]); // re-run when new contact data arrives

    const { hiring_manager, recruiters } = data || {};

    const buildContactPayload = (contact) => ({
        name: contact.name,
        title: contact.title,
        department: contact.department || contact._fullData?.department,
        location: contact.location,
        overall_experience_years: contact._fullData?.overall_experience_years,
        company_experience_years: contact._fullData?.company_experience_years,
        summary: contact._fullData?.summary,
        skills: contact._fullData?.skills,
        education: contact._fullData?.education,
        certifications: contact._fullData?.certifications,
        email: contact.email,
        profile_url: contact.profile_url,
        followers: contact._fullData?.followers,
        confidence_score: contact.confidence_score,
        referral_probability: contact.referral_probability,
        data_source: contact._fullData?.data_source,
        _fullData: contact._fullData
    });

    useEffect(() => {
        if (!orgId) {
            return;
        }

        if (!batchAnalysisComplete) {
            return;
        }

        if (accountStrategy?.data || accountStrategy?.loading) {
            return;
        }

        const hmContacts = hiring_manager?.best_guesses || [];
        const recruiterContacts = recruiters?.best_guesses || [];
        const combinedContacts = [...hmContacts, ...recruiterContacts];

        if (combinedContacts.length === 0) {
            return;
        }

        const jobDetails = {
            companyName: job?.companyName || data?.companyName,
            jobPosition: job?.jobPosition || data?.jobTitle,
            jobLocation: job?.jobLocation,
            jobDesc: job?.jobDesc,
            job_department_if_any: job?.department || null
        };

        const userProfile = user ? {
            name: user.name,
            jobTitle: user.jobTitle || user.currentTitle,
            currentTitle: user.currentTitle,
            experience: user.experience,
            skills: user.skills || [],
            education: user.education || [],
            experience_details: user.experience || [],
            resume: user.resume || '',
            preferredDepartment: user.preferredDepartment || [],
            preferredIndustry: user.preferredIndustry || [],
            previousIndustryYears: user.previousIndustryYears || []
        } : {};

        console.log('🎯 [RecruiterModal] Auto-requesting referral strategy for org:', orgId);

        dispatch(generateReferralStrategy({
            jobId: job?.id || job?._id,
            organizationId: orgId,
            jobDetails,
            userProfile,
            allContacts: combinedContacts.map(buildContactPayload)
        }));
    }, [
        orgId,
        batchAnalysisComplete,
        accountStrategy,
        hiring_manager,
        recruiters,
        job,
        user,
        data,
        dispatch
    ]);

    // Auto-expand the first contact when its analysis becomes available
    useEffect(() => {
        if (!expandedPersonId) {
            const keys = Object.keys(reasoningData || {});
            if (keys.length > 0) {
                setExpandedPersonId(keys[0]);
            }
        }
    }, [reasoningData]);
    
    // Don't block modal if data is missing - Job Acquisition Plan works without recruiters
    // This allows users to see strategy recommendations even when ContactOut has no credits
    if (!data) {
        console.warn('RecruiterModal: No recruiter data provided, but showing Job Acquisition Plan anyway');
    } else {
        console.log('   Hiring Managers:', hiring_manager?.best_guesses?.length || 0);
        console.log('   Recruiters:', recruiters?.best_guesses?.length || 0);
    }

    // Resolve the company label to show in the header. Prefer the explicit job prop, then data, then fall back to organization on recruiter entries.
    const getCompanyLabel = () => {
        if (job?.companyName) return job.companyName;
        if (job?.company?.companyName) return job.company.companyName;
        if (job?.company?.name) return job.company.name;
        if (typeof job?.company === 'string') return job.company;
        if (data?.companyName) return data.companyName;
        if (data?.organizationName) return data.organizationName;
        // Try to extract from first recruiter or hiring manager
        const sample = recruiters?.best_guesses?.[0]?._fullData || hiring_manager?.best_guesses?.[0]?._fullData;
        if (sample) {
            if (sample.organization?.name) return sample.organization.name;
            if (sample.organization_name) return sample.organization_name;
            if (sample.company_name) return sample.company_name;
        }
        return 'Company';
    };
    const companyLabel = getCompanyLabel();

    const deterministicRecommendation = useMemo(() => {
        const signal = {
            source: data?.source || data?.data_source || data?.signalSource,
            hiring_signal_score: data?.hiringSignalScore || data?.overallScore || data?.signalScore
        };
        const recruiterCount = (recruiters?.best_guesses?.length || 0) + (hiring_manager?.best_guesses?.length || 0);
        return recommendStrategy({
            job: job || {},
            signal,
            userProfile: user || {},
            context: { recruiterCount }
        });
    }, [job, data, user, recruiters, hiring_manager]);

    // Helper function to generate initials from name
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.charAt(0).toUpperCase();
    };

    // Helper function to generate a consistent color based on name
    const getAvatarColor = (name) => {
        const colors = [
            'bg-blue-600',
            'bg-purple-600', 
            'bg-green-600',
            'bg-orange-600',
            'bg-pink-600',
            'bg-indigo-600',
            'bg-teal-600',
            'bg-red-600'
        ];
        const hash = name?.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0;
        return colors[hash % colors.length];
    };

    const fetchReferralReasoning = async (person, personId) => {
        if (reasoningData[personId]) {
            // Already fetched (from batch analysis or previous individual fetch)
            console.log(`Analysis already available for ${person.name}, skipping API call`);
            return;
        }

        // If batch analysis is complete, data should already be there
        if (batchAnalysisComplete && !reasoningData[personId]) {
            console.log(`Batch analysis complete but no data for ${person.name}, fetching individually...`);
        }

        setLoadingReasoning(prev => ({ ...prev, [personId]: true }));

        try {
            // Prepare recruiter data from the person object
            const recruiterData = {
                name: person.name,
                title: person.title,
                department: person.department || person._fullData?.department,
                location: person.location,
                overall_experience_years: person._fullData?.overall_experience_years,
                company_experience_years: person._fullData?.company_experience_years,
                email: person.email,
                profile_url: person.profile_url,
                confidence_score: person.confidence_score,
                referral_probability: person.referral_probability
            };

            const jobDetails = {
                companyName: job.companyName,
                jobPosition: job.jobPosition,
                jobLocation: job.jobLocation,
                jobDesc: job.jobDesc
            };

            // Include user profile for better matching
            const userProfile = user ? {
                preferredDepartment: user.preferredDepartment || [],
                preferredIndustry: user.preferredIndustry || [],
                skills: user.skills || [],
                experience: user.experience || null
            } : {};

            console.log('Sending referral reasoning request:', { recruiterData, jobDetails, userProfile });

            const response = await api.post('/api/recruitercontacts/generate-referral-reasoning-direct', {
                recruiterData,
                jobDetails,
                userProfile
            });

            console.log('Received reasoning response:', response.data);

            setReasoningData(prev => ({ 
                ...prev, 
                [personId]: response.data 
            }));
        } catch (error) {
            console.error('Failed to fetch referral reasoning:', error);
            console.error('Error response:', error.response?.data);
            setReasoningData(prev => ({ 
                ...prev, 
                [personId]: { 
                    error: error.response?.data?.message || 'Failed to load analysis. Please try again.' 
                } 
            }));
        } finally {
            setLoadingReasoning(prev => ({ ...prev, [personId]: false }));
        }
    };

    const PersonCard = ({ person, index, type }) => {
        // Use the unique ID we added during categorization (matches batch storage format)
        const personId = person._uniqueId || `${type}-${index}`;
        const isExpanded = expandedPersonId === personId;

        // Auto-fetch individual analysis if batch finished but this person has no data
        useEffect(() => {
            // ✅ CHECK DB-CACHED ANALYSIS FIRST
            if (person.ai_analysis && !reasoningData[personId]) {
                console.log(`💾 Found cached AI analysis in DB for ${person.name}. Using it.`);
                setReasoningData(prev => ({
                    ...prev,
                    [personId]: {
                        ...person.ai_analysis,
                        source: 'cached'
                    }
                }));
                return;
            }

            if (!reasoningData[personId] && batchAnalysisComplete && !loadingReasoning[personId]) {
                // fire-and-forget individual fetch
                fetchReferralReasoning(person, personId);
            }
        }, [batchAnalysisComplete, person.ai_analysis]);

        // Prepare filtered potential concerns to hide trivial/default items
        const rawConcerns = reasoningData[personId]?.reasoning?.potentialConcerns || [];
        const filteredConcerns = rawConcerns
            .map(c => (c || '').toString().trim())
            .filter(c => !!c)
            .filter(c => {
                const lower = c.toLowerCase();
                // Hide exact or semantically matching messages
                if (lower === 'unknown tenure limits network strength assessment') return false;
                if (lower.includes('none identified') && lower.includes('ideal contact profile')) return false;
                // Hide placeholder message like: "None significant for this role type (See <attachments>...)"
                if (lower.includes('none significant for this role type')) return false;
                // if the item is just a bullet or placeholder, hide it
                if (lower === '•' || lower === '-') return false;
                return true;
            });
        
        return (
        <div 
            id={`contact-${personId}`}
            className="rounded-[2.5rem] bg-white border border-slate-200 hover:border-indigo-300 transition-all duration-500 group overflow-hidden shadow-sm hover:shadow-xl hover:shadow-indigo-500/5"
        >
            {/* Compact View - Always Visible */}
            <div className="p-6 pl-5 flex items-start gap-5">
                {/* Profile Photo */}
                {(person?.profile_photo || person?.photo_url || person?.image) ? (
                    <img 
                        className='w-20 h-20 rounded-full object-cover border-3 border-gray-100 shadow-sm flex-shrink-0' 
                        src={person?.profile_photo || person?.photo_url || person?.image} 
                        alt={person.name || 'Profile'}
                        onError={(e) => {
                            // Fallback to initials avatar if image fails to load
                            e.target.style.display = 'none';
                            if (e.target.nextElementSibling) {
                                e.target.nextElementSibling.style.display = 'flex';
                            }
                        }}
                    />
                ) : null}
                {/* Initials Avatar - shown when no profile photo or image load fails */}
                <div 
                    className={`w-20 h-20 rounded-full ${getAvatarColor(person.name)} text-white flex items-center justify-center font-bold text-2xl flex-shrink-0 shadow-sm ${(person?.profile_photo || person?.photo_url || person?.image) ? 'hidden' : ''}`}
                >
                    {getInitials(person.name)}
                </div>

                {/* Main Content */}
                <div className='flex-1 flex flex-col gap-3'>
                    {/* Name and Title Row */}
                    <div className='flex items-start justify-between gap-4'>
                        <div className='flex-1 min-w-0 text-left'>
                            <p
                                className="text-lg font-bold text-[#3F57E0] block truncate mb-1 cursor-default"
                                onClick={(e) => e.stopPropagation()}
                                title={person.name}
                            >
                                {person.name}
                            </p>
                            <p className="text-base font-medium text-gray-800 truncate mb-2">{person.title}</p>
                            {person.location && (
                                <div className="flex items-center gap-1.5 mb-0.5">
                                    <MapPin size={14} className="text-gray-400" />
                                    <p className="text-sm text-gray-600">{person.location}</p>
                                </div>
                            )}
                        </div>
                        
                        {/* Referral Quality Badge */}
                        <div className='flex flex-col gap-1 items-end ml-3'>
                            {typeof person.referral_probability === 'number' && (
                                <Tooltip title={`Referral Score: ${person.referral_probability}% - Based on role, seniority, and ability to influence hiring decisions`}>
                                    <span className={`px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap cursor-help shadow-sm border-2 transition-all ${
                                        person.referral_probability >= 80
                                            ? 'border-green-200 text-green-800 bg-green-50 hover:bg-green-100'
                                            : person.referral_probability >= 60
                                                ? 'border-blue-200 text-blue-800 bg-blue-50 hover:bg-blue-100'
                                                : 'border-yellow-200 text-yellow-800 bg-yellow-50 hover:bg-yellow-100'
                                    }`}>
                                        {person.referral_probability >= 80
                                            ? 'Best'
                                            : person.referral_probability >= 60
                                                ? 'Better'
                                                : 'Good'}
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    {/* Experience Years - Always Visible */}
                    {((typeof person._fullData?.overall_experience_years === 'number' && person._fullData.overall_experience_years > 0) || (typeof person._fullData?.company_experience_years === 'number' && person._fullData.company_experience_years > 0)) && (
                        <div className='flex items-center gap-4 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg'>
                            {(typeof person._fullData?.overall_experience_years === 'number' && person._fullData.overall_experience_years > 0) && (
                                <span className='flex items-center gap-1.5'>
                                    <Briefcase size={15} className="text-blue-500" />
                                    <span className='font-semibold text-gray-900'>{person._fullData.overall_experience_years} years</span>
                                    <span className='text-gray-600'>overall</span>
                                </span>
                            )}
                            {(typeof person._fullData?.company_experience_years === 'number' && person._fullData.company_experience_years > 0) && (
                                <span className='flex items-center gap-1.5'>
                                    <Building size={15} className="text-blue-500" />
                                    <span className='font-semibold text-gray-900'>{person._fullData.company_experience_years} years</span>
                                    <span className='text-gray-600'>at company</span>
                                </span>
                            )}
                        </div>
                    )}

                    {/* Contact action icons */}
                    <div className='flex items-center gap-2'>
                        {person.email && (
                            <Tooltip title={person.email} placement="top" arrow>
                                <button
                                    className='p-2 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100 shadow-sm hover:bg-emerald-100 transition-colors cursor-pointer'
                                    onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(person.email); }}
                                >
                                    <Mail size={16} />
                                </button>
                            </Tooltip>
                        )}
                        {person.profile_url && (
                            <Tooltip title={person.profile_url} placement="top" arrow>
                                <a
                                    href={person.profile_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className='p-2 bg-indigo-50 text-indigo-600 rounded-xl border border-indigo-100 shadow-sm hover:bg-indigo-100 transition-colors'
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <LinkedInSVG />
                                </a>
                            </Tooltip>
                        )}
                    </div>

                    {/* Quick Contact Actions (email/LinkedIn intentionally hidden) */}
                    <div className='flex items-center gap-3 flex-wrap justify-start mt-1'>
                        {/*
                        {person.email && (
                            <a 
                                href={`mailto:${person.email}`} 
                                className='flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Mail size={16} />
                                <span>Email</span>
                            </a>
                        )}
                        {person.profile_url && (
                            <a 
                                href={person.profile_url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className='flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white hover:bg-[#006399] rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <LinkedInSVG />
                                <span>Connect</span>
                            </a>
                        )}
                        */}
                    </div>
                </div>
            </div>

            {/* Analysis Details - shown automatically when available or loading */}
            {(reasoningData[personId] || loadingReasoning[personId]) && (
                <div className='px-4 pb-4 pt-0 border-t border-gray-200 bg-gray-50'>
                    {/* Referral Suitability Analysis Section */}
                    {reasoningData[personId] && (
                        <div className='mt-3 p-5 bg-white/70 backdrop-blur-xl border border-slate-200/70 rounded-2xl text-left shadow-[0_10px_30px_rgba(15,23,42,0.06)]'>
                            <div className='flex items-center gap-2.5 mb-4'>
                                <div className="min-w-0">
                                    <h4 className='text-base font-bold text-slate-900 tracking-tight'>
                                        Referral Suitability
                                    </h4>
                                    <p className="text-xs text-slate-500 font-medium">Personalized signal summary</p>
                                </div>
                                <span className='ml-auto text-[11px] text-slate-600 bg-white/70 border border-slate-200 px-2.5 py-1 rounded-full'>
                                    Generated
                                </span>
                            </div>

                            {reasoningData[personId].error ? (
                                <p className='text-xs text-red-600'>{reasoningData[personId].error}</p>
                            ) : (
                                <div className='space-y-3'>
                                    {/* Overall Assessment */}
                                    <div>
                                        <p className='text-sm text-left text-gray-700 leading-relaxed'>
                                            {reasoningData[personId].reasoning?.overallAssessment || reasoningData[personId].reasoning?.fallbackReasoning?.overallAssessment}
                                        </p>
                                    </div>

                                    {/* Key Strengths */}
                                    {(reasoningData[personId].reasoning?.keyStrengths || []).length > 0 && (
                                        <div>
                                            <p className='text-sm font-semibold text-emerald-800 mb-2 text-left'>Key strengths</p>
                                            <ul className='space-y-1.5 list-disc pl-5 text-left marker:text-slate-300'>
                                                {reasoningData[personId].reasoning.keyStrengths.map((strength, idx) => (
                                                    <li key={idx} className='text-sm text-slate-700 leading-relaxed'>
                                                        {strength}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Potential Concerns */}
                                    {filteredConcerns.length > 0 && (
                                        <div>
                                            <p className='text-sm font-semibold text-amber-800 mb-2 text-left'>Potential concerns</p>
                                            <ul className='space-y-1.5 list-disc pl-5 text-left marker:text-slate-300'>
                                                {filteredConcerns.map((concern, idx) => (
                                                    <li key={idx} className='text-sm text-slate-700 leading-relaxed'>
                                                        {concern}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Networking Strategy */}
                                    {(reasoningData[personId].reasoning?.networkingStrategy || []).length > 0 && (
                                        <div>
                                            <p className='text-sm font-semibold text-indigo-800 mb-2 text-left'>Networking strategy</p>
                                            <ul className='space-y-1.5 list-disc pl-5 text-left marker:text-slate-300'>
                                                {reasoningData[personId].reasoning.networkingStrategy.map((tip, idx) => (
                                                    <li key={idx} className='text-sm text-slate-700 leading-relaxed'>
                                                        {tip}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Calculation Factors */}
                                    {reasoningData[personId].reasoning?.calculationFactors && (
                                        <div className='mt-4 pt-4 border-t border-slate-200/70'>
                                            <p className='text-sm font-semibold text-slate-800 mb-3 text-left'>Scoring breakdown</p>
                                            <div className='grid grid-cols-2 gap-2'>
                                                {Object.entries(reasoningData[personId].reasoning.calculationFactors).map(([key, value]) => (
                                                    <div key={key} className='bg-white/60 border border-slate-200/60 rounded-xl p-3'>
                                                        <p className='text-xs text-slate-600 capitalize'>{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                                                        <div className='flex items-center gap-2 mt-1'>
                                                            <div className='flex-1 bg-slate-200/70 rounded-full h-1.5'>
                                                                <div 
                                                                    className='bg-indigo-600 h-1.5 rounded-full transition-all duration-500'
                                                                    style={{ width: `${value}%` }}
                                                                />
                                                            </div>
                                                            <span className='text-sm font-bold text-indigo-700 tabular-nums'>{value}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Loading State for Suitability Analysis */}
                    {loadingReasoning[personId] && (
                        <div className='mt-3 p-5 bg-white/70 backdrop-blur-xl border border-slate-200/70 rounded-2xl shadow-[0_10px_30px_rgba(15,23,42,0.06)]'>
                            <div className='flex items-center gap-2'>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-indigo-600"></div>
                                <p className='text-xs text-slate-600'>Generating analysis…</p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
        );
    };

    // Helper function to categorize all contacts into tiers and sort by referral probability
    const categorizeContactsByTier = () => {
        // Add unique identifiers to contacts BEFORE categorizing
        const hiringManagerContacts = (hiring_manager?.best_guesses || []).map((person, idx) => ({
            ...person,
            _uniqueId: `hiring_manager-${idx}`, // Same format as batch analysis storage
            _sourceType: 'hiring_manager'
        }));
        
        const recruiterContacts = (recruiters?.best_guesses || []).map((person, idx) => ({
            ...person,
            _uniqueId: `recruiters-${idx}`, // Same format as batch analysis storage
            _sourceType: 'recruiters'
        }));
        
        // Combine and deduplicate based on LinkedIn URL or email (most reliable unique identifiers)
        const combinedContacts = [...hiringManagerContacts, ...recruiterContacts];
        const seenIdentifiers = new Set();
        const allContacts = [];
        
        for (const contact of combinedContacts) {
            // Create a unique key based on available identifiers (profile_url is most reliable)
            const uniqueKey = contact.profile_url || 
                             contact.email || 
                             contact.work_email || 
                             `${contact.name}-${contact.title}`.toLowerCase();
            
            if (!seenIdentifiers.has(uniqueKey)) {
                seenIdentifiers.add(uniqueKey);
                allContacts.push(contact);
            }
        }
        
        if (allContacts.length === 0) return { tier1: [], tier2: [], tier3: [] };
        
        // Filter by tier and sort each tier by referral_probability (highest first)
        // Tier 1: 80%+, Tier 2: 60-79%, Tier 3: <60%
        const tier1 = allContacts
            .filter(r => r.referral_probability >= 80)
            .sort((a, b) => b.referral_probability - a.referral_probability);
        
        const tier2 = allContacts
            .filter(r => r.referral_probability >= 60 && r.referral_probability < 80)
            .sort((a, b) => b.referral_probability - a.referral_probability);
        
        const tier3 = allContacts
            .filter(r => r.referral_probability < 60)
            .sort((a, b) => b.referral_probability - a.referral_probability);
        
        return { tier1, tier2, tier3 };
    };

    const contactTiers = categorizeContactsByTier();
    const handleSaveAndClose = () => {
        if (typeof handleSaveSelectedJobs === 'function') {
            handleSaveSelectedJobs();
        }
        if (typeof onSaveComplete === 'function') {
            onSaveComplete();
        }
        // Scripted tour may advance sub-steps while keeping the modal open; X still calls onClose directly.
        const keepModalOpenForTour = isOnboardingPreview && typeof onSaveComplete === 'function';
        if (typeof onClose === 'function' && !keepModalOpenForTour) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
            onClick={onClose}
        >
            {/* Modal Content — stable anchor for onboarding spotlight (avoid generic div[role=dialog] which may not exist or may match other UI). */}
            <div
                ref={modalContentRef}
                data-onboarding-recruiter-modal="true"
                role="dialog"
                aria-modal="true"
                aria-label={companyLabel ? `${companyLabel} recruiter intelligence` : 'Recruiter intelligence'}
                className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-5xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center border-b pb-3 mb-6">
                    <div className='flex items-center gap-2'>
                        <h2 className="text-2xl font-bold text-gray-800">{companyLabel}</h2>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div>
                    {/* Batch Analysis Status Banner */}
                    {batchAnalysisLoading && (
                        <div className="mb-8 p-6 bg-indigo-50 border border-indigo-100 rounded-[2.5rem] flex items-center gap-4 animate-pulse">
                            <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-black text-indigo-900 uppercase tracking-wider">Analyzing Intelligence...</p>
                                <p className="text-xs text-indigo-600 font-medium">Scanning network contacts for high-probability referral matches</p>
                            </div>
                        </div>
                    )}

                    {/* Account Referral Strategy Section with Tabs */}
                    {batchAnalysisComplete && (
                        <Box sx={{ width: '100%', mb: 8 }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'rgba(0,0,0,0.06)' }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={(e, newValue) => setActiveTab(newValue)}
                                    aria-label="referral strategy tabs"
                                    variant="fullWidth"
                                    sx={{
                                        '& .MuiTabs-indicator': { backgroundColor: '#3F57E0', height: 3, borderRadius: '3px 3px 0 0' },
                                        '& .MuiTab-root': { 
                                            color: '#64748B', 
                                            fontWeight: 900, 
                                            fontSize: '0.75rem', 
                                            textTransform: 'uppercase', 
                                            letterSpacing: '0.1em',
                                            pb: 2
                                        },
                                        '& .Mui-selected': { color: '#3F57E0 !important' }
                                    }}
                                >
                                    <Tab label="Deployment Strategy" icon={<Target size={18} />} iconPosition="start" />
                                    <Tab label="Acquisition Plan" icon={<Zap size={18} />} iconPosition="start" />
                                </Tabs>
                            </Box>
                            
                            {/* Tab Panel 0: Referral Strategy */}
                            {activeTab === 0 && (
                                <Box sx={{ pt: 3 }}>
                                    {accountStrategy?.data ? (
                                        <ReferralStrategy
                                            strategy={accountStrategy.data}
                                            companyName={job?.companyName}
                                            loading={accountStrategy?.loading}
                                            error={accountStrategy?.error}
                                            onSaveForNetworking={handleSaveAndClose}
                                        />
                                    ) : batchOverview?.overallStrategy ? (
                                        // Fallback simple strategy box when no accountStrategy available in Redux
                                        <div className="p-4 bg-white rounded-lg border border-gray-100">
                                            <div className="flex items-start gap-3">
                                                <div className="flex-1">
                                                    <h4 className="text-lg font-semibold text-gray-800">Referral Strategy</h4>
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        {batchOverview.overallStrategy}
                                                    </p>
                                                    {batchOverview.topRecommendations && batchOverview.topRecommendations.length > 0 && (
                                                        <p className="text-xs text-gray-500 mt-3">
                                                            Top picks: {batchOverview.topRecommendations.join(', ')}
                                                        </p>
                                                    )}
                                                </div>
                                                <div>
                                                    <button
                                                        onClick={() => {
                                                            // If user wants the richer strategy stored in backend, we can still trigger the account strategy action if orgId exists
                                                            const orgId = job?.organizationId || job?.companyId || job?._id;
                                                            if (orgId && !accountStrategy) {
                                                                dispatch(generateReferralStrategy({
                                                                    jobId: job?.id || job?._id,
                                                                    organizationId: orgId,
                                                                    jobDetails: {
                                                                        companyName: job?.companyName,
                                                                        jobPosition: job?.jobPosition
                                                                    },
                                                                    userProfile: user || {},
                                                                    allContacts: []
                                                                }));
                                                            }
                                                        }}
                                                        className="text-sm text-blue-600 hover:underline"
                                                    >
                                                        Generate full strategy
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </Box>
                            )}
                            
                            {/* Tab Panel 1: Job Acquisition Plan */}
                            {activeTab === 1 && (
                                <Box sx={{ pt: 3 }}>
                                    <div className="mb-5">
                                        <h3 className="text-sm font-black uppercase tracking-[0.12em] text-slate-500 mb-1">Suggested Strategy</h3>
                                        <p className="text-lg font-bold text-slate-900">
                                            {STRATEGY_NAME_BY_ID[deterministicRecommendation.strategyId] || 'Recommended Strategy'}
                                            <span className="text-sm font-semibold text-indigo-600 ml-2">({deterministicRecommendation.confidence}% match)</span>
                                        </p>
                                        <p className="text-sm text-slate-600 mt-1">{deterministicRecommendation.reason}</p>
                                    </div>
                                    <JobAcquisitionPlan
                                        job={job}
                                        companyName={job?.companyName}
                                        isOnboardingPreview={isOnboardingPreview}
                                        onPreviewActivate={isOnboardingPreview ? handleSaveAndClose : undefined}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* All Contacts Section - Divided by Tiers (Deployment Strategy tab only) */}
                    {data && activeTab === 0 && (
                        <div className="mt-10">
                            {/* Tier 1 - Best (80%+) */}
                            {contactTiers.tier1.length > 0 && (
                                <div className="mb-12">
                                    <div className='flex items-center gap-4 mb-6'>
                                        <div className="flex-1">
                                            <h3 className="text-xl font-black text-slate-900 tracking-tight">Tier 1: VIP Contacts</h3>
                                            <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mt-0.5">80%+ Referral Probability • Primary Targets</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-6">
                                        {contactTiers.tier1.map((person, index) => (
                                            <PersonCard key={`contact-t1-${index}`} person={person} index={`t1-${index}`} type="contact" />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tier 2 - Better (60-79%) */}
                            {contactTiers.tier2.length > 0 && (
                                <div className="mb-12">
                                    <div className='flex items-center gap-4 mb-6'>
                                        <div className="flex-1">
                                            <h3 className="text-xl font-black text-slate-900 tracking-tight">Tier 2: Influence Network</h3>
                                            <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest mt-0.5">60-79% Referral Probability • Strategic Support</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-6">
                                        {contactTiers.tier2.map((person, index) => (
                                            <PersonCard key={`contact-t2-${index}`} person={person} index={`t2-${index}`} type="contact" />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tier 3 - Good (<60%) */}
                            {contactTiers.tier3.length > 0 && (
                                <div className="mb-12">
                                    <div className='flex items-center gap-4 mb-6'>
                                        <div className="flex-1">
                                            <h3 className="text-xl font-black text-slate-900 tracking-tight">Tier 3: Information Contacts</h3>
                                            <p className="text-xs font-bold text-amber-600 uppercase tracking-widest mt-0.5">Below 60% Referral Probability • Intelligence Gathering</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-6">
                                        {contactTiers.tier3.map((person, index) => (
                                            <PersonCard key={`contact-t3-${index}`} person={person} index={`t3-${index}`} type="contact" />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* No contacts message */}
                            {contactTiers.tier1.length === 0 && contactTiers.tier2.length === 0 && contactTiers.tier3.length === 0 && (
                                <div className="p-20 border-4 border-dashed border-slate-100 rounded-[3rem] text-center bg-slate-50/50">
                                    <Target size={48} className="text-slate-200 mx-auto mb-4" />
                                    <h4 className="text-lg font-black text-slate-400">Target Contacts Unidentified</h4>
                                    <p className="text-sm text-slate-400 mt-1 max-w-xs mx-auto">No intelligence data available for individual contacts at this organization yet.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RecruiterModal;